import { type CSSProperties, Fragment, type ReactNode } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { SchoolFillIcon } from '@zep-us/zus-icons';
import Link, { type LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { BellFillIcon, BoltFillIcon, BookCoverFillIcon, ExportIcon, FolderFillIcon, HouseFillIcon, SquareGridFillIcon } from '@/icons';
import { getSupportLinks } from '@/layout/Header/constants/gnbConstants';
import { useUser } from '@/lib/auth';
import { BetaChip } from '@/shared/components/Chip/BetaChip';
import { upsellingModal } from '@/shared/components/UpsellingModal';
import { CLASSROOM_ROUTES } from '@/shared/constants/routes';
import { setReferrer } from '@/shared/features/enter-referrer-tracking';
import { Button } from '@/ui_v3';
import { formatEnglishTitleCase } from '@/utils/formatEnglishTitleCase';
import { cn } from '@/utils/tailwind.util';
import { PRODUCT_STICKY_HEIGHT_CLASS } from '../layout/productLayoutConstants';
type ProductSidebarMenuItem = {
  labelKey: keyof typeof PRODUCT_SIDEBAR_LABEL_T_KEYS;
  href: string;
  activeHrefs: string[];
  Icon: typeof FolderFillIcon;
  rightSlot?: ReactNode;
  hasTopDivider?: boolean;
};
const PRODUCT_SIDEBAR_LABEL_T_KEYS = {
  myQuiz: 'common:myQuiz',
  myBoard: 'common:myBoard',
  'classroom.myClassrooms': 'common:classroom.myClassrooms'
} as const;
const PRODUCT_SIDEBAR_LOGIN_TARGETS: Record<keyof typeof PRODUCT_SIDEBAR_LABEL_T_KEYS, string> = {
  myQuiz: 'myquiz',
  myBoard: 'my-board',
  'classroom.myClassrooms': 'classrooms'
};
const betaChip = <BetaChip variant="outlined" className="h-[18px] min-h-[18px] shrink-0 border-yellow-600 bg-yellow-50 px-3xs text-caption-md-semibold text-yellow-900" />;
const menuItems: ProductSidebarMenuItem[] = [{
  labelKey: 'myQuiz',
  href: '/myquiz',
  activeHrefs: ['/myquiz'],
  Icon: BookCoverFillIcon
}, {
  labelKey: 'myBoard',
  href: '/my-board',
  activeHrefs: ['/my-board'],
  Icon: SquareGridFillIcon,
  rightSlot: betaChip
}, {
  labelKey: 'classroom.myClassrooms',
  href: CLASSROOM_ROUTES.list(),
  activeHrefs: ['/classrooms'],
  Icon: SchoolFillIcon,
  rightSlot: betaChip,
  hasTopDivider: true
}];
export const ProductSidebar = ({
  className,
  style,
  position = 'sticky'
}: {
  className?: string;
  style?: CSSProperties;
  position?: 'sticky' | 'fixed';
}) => {
  const {
    user,
    isInitialized,
    isAuthenticated
  } = useUser();
  const {
    t,
    i18n
  } = useTranslation(['common']);
  const router = useRouter();
  const isLoggedIn = isInitialized && isAuthenticated;
  // 학생은 플랜을 구매하는 주체가 아니다(결제는 선생님·워크스페이스 소유자 몫).
  const isStudent = user?.type?.toLowerCase() === 'student';
  const shouldShowUpgradeButton = isLoggedIn && !isStudent && user?.planType !== 'PRO';
  const isPublicActive = router.pathname.startsWith('/public');
  return <aside style={style} className={cn(position === 'fixed' ? 'fixed' : 'sticky', PRODUCT_STICKY_HEIGHT_CLASS, 'w-[260px] shrink-0 overflow-hidden bg-default pb-[40px] pl-[40px] pr-[16px] pt-[12px]', className)} data-sentry-component="ProductSidebar" data-sentry-source-file="ProductSidebar.tsx">
      <nav className="flex h-full w-full flex-col gap-lg">
        <div className="flex w-full flex-col gap-lg">
          <div className="flex w-full flex-col gap-xs">
            <SidebarMenuItem label={formatEnglishTitleCase(t('common:common.quizExplore'), i18n.resolvedLanguage)} href="/public" Icon={FolderFillIcon} isActive={isPublicActive} isEmphasized data-sentry-element="SidebarMenuItem" data-sentry-source-file="ProductSidebar.tsx" />
            <div className="h-px w-full border-t border-alternative" />
            {menuItems.map(item => <Fragment key={item.labelKey}>
                {item.hasTopDivider && <div className="h-px w-full border-t border-alternative" />}
                <SidebarMenuItem label={formatEnglishTitleCase(t(PRODUCT_SIDEBAR_LABEL_T_KEYS[item.labelKey]), i18n.resolvedLanguage)} href={isLoggedIn ? item.href : {
              pathname: '/login',
              query: {
                callbackUrl: item.href
              }
            }} Icon={item.Icon} rightSlot={item.rightSlot} isActive={item.activeHrefs.some(href => router.pathname.startsWith(href))} isMuted={!isLoggedIn} onClick={isLoggedIn ? undefined : () => {
              logger.track('enter_login', {
                location: 'public_sidebar',
                target: PRODUCT_SIDEBAR_LOGIN_TARGETS[item.labelKey]
              });
            }} />
              </Fragment>)}
          </div>
          {shouldShowUpgradeButton && <UpgradeButton />}
          {!isLoggedIn && <div className="flex w-full flex-col gap-xs">
              <LoginButton />
              <PricingGuideButton />
            </div>}
        </div>
        {isLoggedIn ? <LoggedInSidebarFooter /> : <SidebarFooter countryCode="ETC" showPlanManagement={false} />}
      </nav>
    </aside>;
};
const LoggedInSidebarFooter = () => {
  const {
    user
  } = useUser();
  return <SidebarFooter countryCode={user?.countryCode ?? 'ETC'} showPlanManagement data-sentry-element="SidebarFooter" data-sentry-component="LoggedInSidebarFooter" data-sentry-source-file="ProductSidebar.tsx" />;
};
const SidebarFooter = ({
  countryCode,
  showPlanManagement
}: {
  countryCode: string;
  showPlanManagement: boolean;
}) => {
  const {
    t,
    i18n
  } = useTranslation(['common']);
  const supportLinks = getSupportLinks(countryCode);
  const footerIconFill = ICON_COLOR.assistive;
  const footerMenuItemClassName = '!px-[8px] !text-alternative hover:!text-alternative focus:!text-alternative';
  return <div className="mt-auto flex w-full flex-col gap-[6px]" data-sentry-component="SidebarFooter" data-sentry-source-file="ProductSidebar.tsx">
      {showPlanManagement && <SidebarMenuItem label={formatEnglishTitleCase(t('common:planManage'), i18n.resolvedLanguage)} href="/profile/plan" Icon={BoltFillIcon} isMuted iconFill={footerIconFill} className={footerMenuItemClassName} size="compact" />}
      <SidebarMenuItem label={formatEnglishTitleCase(t('common.header.updateNews'), i18n.resolvedLanguage)} href={supportLinks.updateNews} Icon={BellFillIcon} rightSlot={<ExternalLinkIcon fill={footerIconFill} />} isExternal isMuted iconFill={footerIconFill} className={footerMenuItemClassName} size="compact" data-sentry-element="SidebarMenuItem" data-sentry-source-file="ProductSidebar.tsx" />
      <SidebarMenuItem label={formatEnglishTitleCase(t('common.header.aboutZepQuiz'), i18n.resolvedLanguage)} href="/landing" Icon={HouseFillIcon} rightSlot={<ExternalLinkIcon fill={footerIconFill} />} isExternal isMuted iconFill={footerIconFill} className={footerMenuItemClassName} size="compact" data-sentry-element="SidebarMenuItem" data-sentry-source-file="ProductSidebar.tsx" />
    </div>;
};
export const ProductBottomNavigation = () => {
  const {
    isInitialized,
    isAuthenticated
  } = useUser();
  const {
    t,
    i18n
  } = useTranslation(['common']);
  const router = useRouter();
  const isLoggedIn = isInitialized && isAuthenticated;
  const isPublicActive = router.pathname.startsWith('/public');
  const items = [{
    label: formatEnglishTitleCase(t('common:common.quizExplore'), i18n.resolvedLanguage),
    href: '/public',
    activeHrefs: ['/public'],
    Icon: FolderFillIcon,
    isActive: isPublicActive
  }, ...menuItems.map(item => ({
    ...item,
    label: formatEnglishTitleCase(t(PRODUCT_SIDEBAR_LABEL_T_KEYS[item.labelKey]), i18n.resolvedLanguage),
    href: isLoggedIn ? item.href : {
      pathname: '/login',
      query: {
        callbackUrl: item.href
      }
    },
    isActive: item.activeHrefs.some(href => router.pathname.startsWith(href))
  }))];
  return <nav className="fixed bottom-0 left-0 right-0 z-40 px-sm pb-[calc(12px+env(safe-area-inset-bottom))] tablet-sm:hidden" data-sentry-component="ProductBottomNavigation" data-sentry-source-file="ProductSidebar.tsx">
      <div className="flex w-full items-center justify-center rounded-full border border-opacity bg-opacity-white-10 p-2xs backdrop-blur-[5px]">
        {items.map(item => {
        const iconFill = item.isActive ? ICON_COLOR.strong : ICON_COLOR.alternative;
        return <Link key={item.activeHrefs[0]} href={item.href} className={cn('flex min-w-0 flex-1 flex-col items-center gap-[2px] rounded-full p-3xs text-caption-md-semibold', item.isActive ? 'text-strong' : 'text-alternative', item.isActive && 'bg-opacity-violet-2')} prefetch={false}>
              <item.Icon size="md" fill={iconFill} className="shrink-0" />
              <span className="max-w-full truncate">{String(item.label)}</span>
            </Link>;
      })}
      </div>
    </nav>;
};
const SidebarMenuItem = ({
  label,
  href,
  Icon,
  iconFill: iconFillProp,
  rightSlot,
  className: classNameProp,
  isActive = false,
  isMuted = false,
  isEmphasized = false,
  isExternal = false,
  size = 'default',
  onClick
}: {
  label: string;
  href: LinkProps['href'];
  Icon: typeof FolderFillIcon;
  iconFill?: string;
  rightSlot?: ReactNode;
  className?: string;
  isActive?: boolean;
  isMuted?: boolean;
  isEmphasized?: boolean;
  isExternal?: boolean;
  size?: 'default' | 'compact';
  onClick?: () => void;
}) => {
  const iconFill = iconFillProp ?? (isMuted ? ICON_COLOR.disabled : isActive ? ICON_COLOR.strong : ICON_COLOR.alternative);
  const className = cn('flex w-full shrink-0 items-center justify-start gap-xs overflow-hidden rounded-sm border-0 bg-transparent pl-sm pr-md text-left hover:bg-neutral', size === 'compact' ? 'h-[32px] min-h-[32px] text-body-md-semibold' : 'h-[46px] min-h-[46px]', size === 'default' && (isEmphasized ? 'py-sm text-body-md-semibold' : 'py-xs text-body-md-semibold'), isMuted ? 'text-assistive' : isActive ? 'bg-neutral text-strong' : 'text-alternative', classNameProp);
  const content = <>
      <Icon size={size === 'compact' ? 'xs' : 'sm'} fill={iconFill} className="shrink-0" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {rightSlot}
    </>;
  return <Link href={href} className={className} target={isExternal ? '_blank' : undefined} rel={isExternal ? 'noopener noreferrer' : undefined} onClick={onClick} prefetch={isExternal ? false : undefined} data-sentry-element="Link" data-sentry-component="SidebarMenuItem" data-sentry-source-file="ProductSidebar.tsx">
      {content}
    </Link>;
};
const ExternalLinkIcon = ({
  fill
}: {
  fill: string;
}) => <ExportIcon size="xs" fill={fill} className="shrink-0" data-sentry-element="ExportIcon" data-sentry-component="ExternalLinkIcon" data-sentry-source-file="ProductSidebar.tsx" />;
const UpgradeButton = () => {
  const {
    t
  } = useTranslation(['common']);
  return <Button className="w-full" size="sm" intent="gradient" onClick={() => {
    upsellingModal.open({
      initialPlanType: 'PRO',
      from: 'public_sidebar'
    });
  }} data-sentry-element="Button" data-sentry-component="UpgradeButton" data-sentry-source-file="ProductSidebar.tsx">
      <BoltFillIcon size="xs" className="shrink-0" data-sentry-element="BoltFillIcon" data-sentry-source-file="ProductSidebar.tsx" />
      <span>{t('common:planUpgrade')}</span>
    </Button>;
};
const LoginButton = () => {
  const {
    t
  } = useTranslation(['common']);
  return <Button className="w-full" size="sm" intent="strong" asChild data-sentry-element="Button" data-sentry-component="LoginButton" data-sentry-source-file="ProductSidebar.tsx">
      <Link
    // callbackUrl 을 박지 않는다 — 로그인 후 목적지는 role 기본값
    // (학생 → /classrooms, 그 외 → /public)이 정한다.
    href="/login" onClick={() => {
      logger.track('enter_login', {
        location: 'public_sidebar'
      });
    }} prefetch={false} data-sentry-element="Link" data-sentry-source-file="ProductSidebar.tsx">
        {t('common:header.login')}
      </Link>
    </Button>;
};
const PricingGuideButton = () => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  return <Button className="w-full" size="sm" variant="contained" intent="neutral" asChild data-sentry-element="Button" data-sentry-component="PricingGuideButton" data-sentry-source-file="ProductSidebar.tsx">
      <Link href="/pricing" target="_blank" rel="noopener noreferrer" onClick={() => {
      setReferrer({
        from: router.asPath,
        location: 'public_sidebar'
      });
    }} prefetch={false} data-sentry-element="Link" data-sentry-source-file="ProductSidebar.tsx">
        {t('common.header.pricingGuide')}
      </Link>
    </Button>;
};